import ky, { type KyInstance } from 'ky';

import {
    BusinessError,
    isBusinessErrorBody,
    transformToApiError,
} from '@/lib/errors';

import type { ApiClientOptions, AuthedApiClientOptions } from './types';

const DEFAULT_OPTIONS: ApiClientOptions = {
    timeout: 30000,
    retryLimit: 2,
    retryMethods: ['get'],
};

export function createApiClient(
    baseUrl: string,
    options: ApiClientOptions = {},
): KyInstance {
    const config = { ...DEFAULT_OPTIONS, ...options };

    return ky.create({
        prefixUrl: baseUrl,
        credentials: config.credentials,
        timeout: config.timeout,
        headers: {
            'Content-Type': 'application/json',
            ...config.headers,
        },
        retry: {
            limit: config.retryLimit ?? 2,
            methods: config.retryMethods ?? ['get'],
            statusCodes: [408, 413, 429, 500, 502, 503, 504],
        },
        hooks: {
            beforeError: [
                async (error) => {
                    throw await transformToApiError(error);
                },
            ],
        },
    });
}

/**
 * Creates the application-token client. HTTP 401 invalidates the complete
 * local app session, while HTTP 428 preserves it and requests fresh user-side
 * trading authority. `beforeSignerMutation` is a UX preflight only; MoonHub
 * and RustSigner still perform the authoritative checks.
 */
export function createAuthedApiClient(
    baseUrl: string,
    getToken: () => string | null,
    onUnauthorized: () => void,
    options: ApiClientOptions = {},
): KyInstance {
    const config: AuthedApiClientOptions = {
        ...DEFAULT_OPTIONS,
        credentials: 'include',
        ...options,
        getToken,
        onUnauthorized,
    };

    return ky.create({
        prefixUrl: baseUrl,
        credentials: config.credentials,
        timeout: config.timeout,
        headers: {
            'Content-Type': 'application/json',
            ...config.headers,
        },
        retry: {
            limit: config.retryLimit ?? 2,
            methods: config.retryMethods ?? ['get'],
            statusCodes: [408, 413, 429, 500, 502, 503, 504],
        },
        hooks: {
            beforeRequest: [
                async (request) => {
                    const token = config.getToken();
                    if (token) {
                        request.headers.set('Authorization', `Bearer ${token}`);
                    }
                    await config.beforeSignerMutation?.(request);
                },
            ],
            afterResponse: [
                async (_request, _options, response) => {
                    if (response.status === 401) {
                        config.onUnauthorized();
                        return response;
                    }
                    if (response.status === 428) {
                        config.onTradingAuthorityRequired?.();
                        return response;
                    }

                    if (response.ok) {
                        const contentType =
                            response.headers.get('content-type');
                        if (contentType?.includes('application/json')) {
                            try {
                                const body = await response.clone().json();
                                if (isBusinessErrorBody(body)) {
                                    throw new BusinessError(body.error);
                                }
                            } catch (error) {
                                if (error instanceof BusinessError) {
                                    throw error;
                                }
                            }
                        }
                    }

                    return response;
                },
            ],
            beforeError: [
                async (error) => {
                    throw await transformToApiError(error);
                },
            ],
        },
    });
}
